import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Banknote, AlertTriangle, CheckCircle2, Plus, ArrowLeft,
} from "lucide-react";

const fmtINR = (n) => (n ? "₹" + Number(n).toLocaleString("en-IN") : "₹0");

const STATUS_BADGES = {
  pending_hr: { label: "Pending HR", cls: "bg-amber-100 text-amber-800" },
  pending_senior: { label: "Pending Senior Mgmt", cls: "bg-blue-100 text-blue-700" },
  pending_disbursement: { label: "Approved · Awaiting Payment", cls: "bg-purple-100 text-purple-700" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
  recovered: { label: "Recovered", cls: "bg-slate-100 text-slate-700" },
};

export function MyAdvancesList() {
  const navigate = useNavigate();
  const [advances, setAdvances] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/advances?scope=mine"),
      api.get("/advances/eligibility"),
    ]).then(([adv, elig]) => {
      setAdvances(adv.data || []);
      setEligibility(elig.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="my-advances">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Banknote className="h-3 w-3" /> My Portal
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            💵 Salary Advance
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Apply for a salary advance. Max 30% of in-hand salary, available after 18th of each month.</p>
        </div>
        <Button
          onClick={() => navigate("/my-portal/advances/new")}
          disabled={!eligibility?.can_apply}
          className="brand-bg brand-bg-hover text-white"
          size="sm"
          data-testid="new-advance-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Apply for Advance
        </Button>
      </div>

      {/* Eligibility card */}
      {eligibility && (
        <div className={`border rounded-lg p-3 ${eligibility.can_apply ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="eligibility-card">
          <div className="flex items-center gap-2 mb-2">
            {eligibility.can_apply ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            )}
            <h3 className="text-sm font-bold">
              {eligibility.can_apply ? "You're eligible to apply" : "Currently not eligible"}
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">In-Hand Salary</div><div className="font-bold">{fmtINR(eligibility.in_hand_salary)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Max Eligible ({eligibility.max_advance_pct}%)</div><div className="font-bold text-emerald-700">{fmtINR(eligibility.max_eligible_amount)}</div></div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Today's Date</div>
              <div className={`font-bold ${eligibility.date_ok ? "text-emerald-700" : "text-red-600"}`}>
                Day {eligibility.today_day} (min {eligibility.min_day_required})
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Pending Advances</div>
              <div className={`font-bold ${eligibility.pending_advances_count === 0 ? "text-emerald-700" : "text-amber-700"}`}>{eligibility.pending_advances_count}</div>
            </div>
          </div>
        </div>
      )}

      {/* Advances list */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {advances.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <Banknote className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No advance requests yet.</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-2 font-semibold">Request #</th>
                <th className="px-3 py-2 font-semibold">Date</th>
                <th className="px-3 py-2 font-semibold text-right">Requested</th>
                <th className="px-3 py-2 font-semibold text-right">Approved</th>
                <th className="px-3 py-2 font-semibold">Reason</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Voucher</th>
              </tr>
            </thead>
            <tbody>
              {advances.map((a) => {
                const badge = STATUS_BADGES[a.overall_status] || { label: a.overall_status, cls: "bg-slate-100" };
                return (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`adv-row-${a.id}`}>
                    <td className="px-3 py-1.5 font-mono text-[10px]">{a.request_number}</td>
                    <td className="px-3 py-1.5 text-slate-600">{(a.application_date || "").slice(0, 10)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtINR(a.amount_requested)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold">{a.amount_approved ? fmtINR(a.amount_approved) : "—"}</td>
                    <td className="px-3 py-1.5 text-slate-600">{a.reason_category}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-3 py-1.5">
                      {a.voucher_number ? (
                        <a
                          href={`/api/advances/${a.id}/voucher-pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-600 hover:underline font-semibold"
                        >
                          {a.voucher_number}
                        </a>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function MyAdvancesNew() {
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [form, setForm] = useState({
    amount_requested: "",
    reason_category: "",
    reason_description: "",
    repayment_months: 1,
    payment_mode: "Cash",
    deduction_start_month: "",  // YYYY-MM
  });
  const [submitting, setSubmitting] = useState(false);

  // Default deduction start = next month
  useEffect(() => {
    if (!form.deduction_start_month) {
      const d = new Date();
      d.setMonth(d.getMonth() + 1);
      setForm((f) => ({ ...f, deduction_start_month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` }));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    api.get("/advances/eligibility")
      .then(({ data }) => setEligibility(data))
      .catch(() => toast.error("Cannot load eligibility"));
  }, []);

  const submit = async () => {
    if (!form.amount_requested || parseFloat(form.amount_requested) <= 0) return toast.error("Enter amount");
    if (!form.reason_category) return toast.error("Select reason category");
    if ((form.reason_description || "").length < 5) return toast.error("Reason description (min 5 chars)");
    setSubmitting(true);
    try {
      const { data } = await api.post("/advances", {
        ...form,
        amount_requested: parseFloat(form.amount_requested),
      });
      toast.success(`Advance request ${data.request_number} submitted!`);
      navigate("/my-portal/advances");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="apply-advance">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/my-portal/advances")}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Apply for Salary Advance</h1>
        </div>
      </div>

      {eligibility && !eligibility.can_apply && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <b>Not eligible right now.</b>
            {!eligibility.date_ok && <div>You can apply only on or after the {eligibility.min_day_required}th of the month.</div>}
            {eligibility.pending_advances_count > 0 && <div>You already have a pending advance.</div>}
            {eligibility.in_hand_salary === 0 && <div>No in-hand salary computed. Contact HR.</div>}
          </div>
        </div>
      )}

      {eligibility?.can_apply && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="apply-form">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs flex justify-between">
            <span>In-Hand: <b>{fmtINR(eligibility.in_hand_salary)}</b></span>
            <span>Max Eligible: <b className="text-emerald-700">{fmtINR(eligibility.max_eligible_amount)}</b></span>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Amount Requested (₹) *</Label>
            <Input
              type="number"
              step="100"
              max={eligibility.max_eligible_amount}
              value={form.amount_requested}
              onChange={(e) => setForm({ ...form, amount_requested: e.target.value })}
              className="mt-1"
              data-testid="amount-input"
              placeholder={`Max ₹${eligibility.max_eligible_amount.toLocaleString("en-IN")}`}
            />
            {form.amount_requested > eligibility.max_eligible_amount && (
              <div className="text-[11px] text-red-600 mt-1">⚠ Exceeds max eligible</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason Category *</Label>
              <Select value={form.reason_category} onValueChange={(v) => setForm({ ...form, reason_category: v })}>
                <SelectTrigger className="mt-1" data-testid="reason-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {(eligibility.reason_options || []).map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Repayment EMIs *</Label>
              <Select value={String(form.repayment_months)} onValueChange={(v) => setForm({ ...form, repayment_months: parseInt(v) })}>
                <SelectTrigger className="mt-1" data-testid="emi-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: eligibility.max_emis }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} month{n > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Description *</Label>
            <Textarea
              value={form.reason_description}
              onChange={(e) => setForm({ ...form, reason_description: e.target.value })}
              rows={3}
              placeholder="Briefly explain why you need this advance"
              data-testid="description-input"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Preferred Payment Mode</Label>
            <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger className="mt-1 w-48" data-testid="mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="Bank">Bank Transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-blue-50/40 border border-blue-200 rounded p-2.5" data-testid="deduction-month-section">
            <Label className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold">
              Deduction Start Month *
            </Label>
            <Input
              type="month"
              value={form.deduction_start_month}
              onChange={(e) => setForm({ ...form, deduction_start_month: e.target.value })}
              className="mt-1 max-w-[200px]"
              data-testid="deduction-month-input"
            />
            <p className="text-[10px] text-slate-600 mt-1">
              From which month should the repayment EMI start deducting from your salary? Default = next month.
              You can also choose <b>past month</b> if it's an advance for a salary already due (e.g., applying on 1 May for April salary).
            </p>
          </div>

          {form.amount_requested > 0 && form.repayment_months > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
              💡 If approved, ~ <b>₹{Math.round(parseFloat(form.amount_requested || 0) / form.repayment_months).toLocaleString("en-IN")}</b> will be deducted per month for {form.repayment_months} month(s).
            </div>
          )}

          <Button
            onClick={submit}
            disabled={submitting}
            className="brand-bg brand-bg-hover text-white w-full"
            data-testid="submit-btn"
          >
            {submitting ? "Submitting…" : "Submit Advance Request"}
          </Button>
        </div>
      )}
    </div>
  );
}
